import * as turf from "@turf/turf";
import type { GameClient, QType, Question, Msg } from "../net";
import { cryptoId } from "../net";
import type { GameMap } from "../map";
import { geocodeArea, nearestCorner, photoUrl } from "../api";
import { radarEliminate, thermometerEliminate } from "../elimination";
import type { LngLat } from "../elimination";
import { CATEGORIES, CAT_BY_ID, matchingCategories, measuringCategories, nearestOf, divisionContaining, measuringEliminate, matchingEliminate, fmtDist } from "../landmarks";
import { enabledPhotoTypes, PHOTO_BY_ID } from "../photoTypes";

// The real Jet Lag question system. A seeker (the TEAM LEADER) picks one of four
// question TYPES; the app computes the info the hider needs and pre-fills an
// EDITABLE question; the leader can edit it, copy it (to paste into WhatsApp), and
// send it to the hider in-app. A shared QUESTION LOG records every question +
// answer + elimination for the whole team. Answers can come from the hider in-app
// OR be recorded by the leader (when the hider replies out-of-band). One-tap Apply
// runs the elimination on the active board (or manual mode leaves it to the human).

const RADAR_DISTS = [{ l: "¼", m: 0.25 }, { l: "½", m: 0.5 }, { l: "1", m: 1 }, { l: "3", m: 3 }, { l: "5", m: 5 }];
const THERMO_DISTS = [{ l: "½ mi", m: 0.5 }, { l: "3 mi", m: 3 }];
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
const AUTO_KEY = (g: string) => `jl_autoapply_${g}`;
const APPLIED_KEY = (g: string) => `jl_applied_${g}`;
const ANSWER_LABELS: Partial<Record<QType, [string, string][]>> = {
  matching: [["yes", "Yes — same"], ["no", "No — different"]],
  measuring: [["closer", "Closer"], ["further", "Further"]],
  thermometer: [["hotter", "🔥 Hotter"], ["colder", "❄️ Colder"]],
  radar: [["yes", "Yes — within"], ["no", "No — outside"]],
};

export interface AskTab { enter(): void; leave(): void }

export function mountAsk(mount: HTMLElement, client: GameClient, gameMap: GameMap, opts?: { setFullscreen?: (on: boolean) => void }): AskTab {
  let type: QType | null = null;
  let catId = "museum";
  let refMode: "gps" | "pin" | "addr" = "gps";
  let ref: LngLat | null = null;
  let refLabel = "";
  let radarDist = 1;
  let radarCustom = false;                  // a free-distance "custom" radar
  let photoType = "";                      // selected photo-request id
  let tA: LngLat | null = null, tB: LngLat | null = null;
  let gpsWatch: number | null = null;
  let draft = "";                          // the editable question text
  let cornerSeq = 0;                       // guards out-of-order corner lookups
  let lastResolved: LngLat | null = null;  // last GPS point we reverse-geocoded
  let tPlace = "";                         // thermometer start corner

  mount.innerHTML = `<div id="ask-builder"></div><div id="ask-log"></div>`;
  const builderEl = mount.querySelector<HTMLElement>("#ask-builder")!;
  const logEl = mount.querySelector<HTMLElement>("#ask-log")!;

  const applied = new Set<string>(JSON.parse(localStorage.getItem(APPLIED_KEY(client.gameId)) || "[]"));
  const persistApplied = () => localStorage.setItem(APPLIED_KEY(client.gameId), JSON.stringify([...applied]));
  const autoApply = () => localStorage.getItem(AUTO_KEY(client.gameId)) !== "0";
  const setAutoApply = (on: boolean) => { localStorage.setItem(AUTO_KEY(client.gameId), on ? "1" : "0"); renderLog(); };
  const nameOf = (sub?: string | null) => sub === client.identity.sub ? "you" : (client.team?.members.find((m) => m.sub === sub)?.name ?? client.pod?.names?.[sub ?? ""] ?? "the leader");

  const stopGps = () => { if (gpsWatch != null) { navigator.geolocation.clearWatch(gpsWatch); gpsWatch = null; } };
  function clearMap() { gameMap.clearReferenceMarker(); gameMap.clearToolMarkers(); gameMap.clearPreviewGeometry(); }
  // Hit by the map's "Exit tool" button: drop the reference/thermo pins and let
  // the builder prompt for a fresh location again.
  function resetTool() { stopGps(); ref = null; refLabel = ""; tA = null; tB = null; tPlace = ""; lastResolved = null; draft = ""; clearMap(); renderBuilder(); }

  // ---- reference point ---------------------------------------------------
  // Reverse-geocode `ll` to the nearest street corner and write it into refLabel
  // (so the question + clue record where the seeker asked from).
  async function resolveCorner(ll: LngLat, emoji: string) {
    const seq = ++cornerSeq;
    refLabel = `${emoji} locating corner…`; recompute();
    const c = await nearestCorner(ll[1], ll[0]);
    if (seq !== cornerSeq) return;                 // a newer reference superseded this
    refLabel = `${emoji} ${c || "dropped pin"}`; recompute();
  }
  function setRef(ll: LngLat, label: string) {
    stopGps(); ref = ll; refLabel = label; gameMap.onToolExit = resetTool;
    gameMap.setReferenceMarker(ll, refMode === "pin", (p) => { ref = p; resolveCorner(p, "📌"); });
    if (refMode === "pin") resolveCorner(ll, "📌"); else recompute();
  }
  function pickRef(mode: "gps" | "pin" | "addr") {
    refMode = mode;
    if (mode === "pin") { const c = gameMap.map.getCenter(); setRef([c.lng, c.lat], "📌 dropped pin"); }
    else if (mode === "gps") {
      lastResolved = null; let firstFix = true; renderBuilder();
      if (!navigator.geolocation) { refLabel = "no GPS — use Pin/Address"; renderBuilder(); return; }
      gpsWatch = navigator.geolocation.watchPosition(
        (p) => {
          ref = [p.coords.longitude, p.coords.latitude]; gameMap.onToolExit = resetTool;
          gameMap.setReferenceMarker(ref, false);
          // Centre on the FIRST fix only; after that leave the map where the seeker
          // put it, so they can zoom/pan out freely with the pin staying in place
          // (every GPS tick used to re-zoom to 14 and fight the user).
          if (firstFix) { firstFix = false; gameMap.flyAboveSheet(ref, 14); }
          // Resolve the corner on first fix and whenever we've moved ~80 m+.
          if (!lastResolved || turf.distance(lastResolved, ref, { units: "meters" }) > 80) { lastResolved = ref; resolveCorner(ref, "📍"); }
          else recompute();
        },
        () => { refLabel = "couldn't get GPS — use Pin/Address"; renderBuilder(); }, { enableHighAccuracy: true, maximumAge: 8000, timeout: 12000 });
    } else renderBuilder();
  }
  async function findAddress(q: string) { try { const { name, geometry } = await geocodeArea(q); const c = turf.centroid(geometry as turf.AllGeoJSON).geometry.coordinates as LngLat; refMode = "addr"; setRef(c, `🔤 ${name || q}`); gameMap.flyAboveSheet(c, 14); } catch { alert("Couldn't find that place."); } }
  function redraw() {
    gameMap.clearPreviewGeometry();
    if (type === "radar" && ref) gameMap.setPreviewGeometry([turf.circle(ref, radarDist, { units: "miles", steps: 64 })]);
    else if ((type === "matching" || type === "measuring") && ref && CAT_BY_ID[catId].kind !== "division") { const n = nearestOf(catId, ref[0], ref[1]); if (n) gameMap.setPreviewGeometry([turf.lineString([ref, n.point])]); }
  }
  // Param change → recompute the default text + redraw + re-render the builder.
  function recompute() { draft = computeText(); redraw(); renderBuilder(); }

  // ---- compute the question text + structured params ---------------------
  function computeText(): string {
    if (type === "photo") { const p = photoType ? PHOTO_BY_ID[photoType] : null; return p ? `📷 Photo request — ${p.name}. ${p.sd}` : ""; }
    if (type === "radar") { const raw = refLabel.replace(/^[^\w]+\s*/, "").trim(); const at = raw && !/locating|use Pin|couldn't|no GPS/i.test(raw) ? raw : "my spot"; return `Are you within ${fmtDist(radarDist)} of ${at}?`; }
    if (type === "thermometer") { const d = tA && tB ? turf.distance(tA, tB, { units: "miles" }) : 0; return tA && tB ? `I've traveled ${fmtDist(d)}. Am I hotter or colder?` : ""; }
    if (!ref) return "";
    const cat = CAT_BY_ID[catId];
    if (type === "matching") {
      if (cat.kind === "division") { const d = divisionContaining(cat.divId!, ref[0], ref[1]); return `Are you in the same ${cat.label.replace(/ \(.*\)/, "").toLowerCase()} as me? (Mine: ${d?.name ?? "—"})`; }
      const n = nearestOf(catId, ref[0], ref[1]); return `Is your nearest ${cat.label.toLowerCase()} the same as mine? (Mine: ${n?.name ?? "—"}, ${n ? fmtDist(n.dist) : "—"})`;
    }
    if (type === "measuring") {
      const n = nearestOf(catId, ref[0], ref[1]); const noun = cat.kind === "division" ? `${cat.label.replace(/ \(.*\)/, "").toLowerCase()} border` : `a ${cat.label.toLowerCase()}`;
      return `Compared to me, are you closer to or further from ${noun}? (Mine: ${n?.name ?? "—"}, ${n ? fmtDist(n.dist) : "—"})`;
    }
    return "";
  }
  function currentQuestion(): Question | null {
    const id = cryptoId();
    if (type === "photo") return photoType ? { id, type, photoType } : null;
    // The corner the seeker asked from (e.g. "E 53rd St & 3rd Ave") — recorded on the clue.
    const raw = refLabel.replace(/^[^\w]+\s*/, "").trim();
    const place = raw && !/locating|use Pin|couldn't|no GPS/i.test(raw) ? raw : undefined;
    if (type === "radar") return ref ? { id, type, ref, dist: radarDist, place } : null;
    if (type === "thermometer") return tA && tB ? { id, type, a: tA, b: tB, place: tPlace || undefined } : null;
    if (type === "matching") return ref ? { id, type, catId, ref, place } : null;
    if (type === "measuring") { if (!ref) return null; const n = nearestOf(catId, ref[0], ref[1]); return { id, type, catId, ref, dist: n?.dist, place }; }
    return null;
  }
  function ready(): boolean { return type === "thermometer" ? !!(tA && tB) : type === "photo" ? !!photoType : !!ref; }

  function copy(text: string) {
    navigator.clipboard?.writeText(text).then(() => { /* ok */ }).catch(() => prompt("Copy this question:", text));
  }
  function send() {
    if (!client.isLeader) return;
    const q = currentQuestion(); const text = draft.trim();
    if (!q || !text) return;
    // Re-asking a previously-vetoed slot doubles the hider's card draw.
    const { used, vetoed } = questionUsage();
    const idn = qIdentity(q);
    if (idn && used.has(idn)) { alert("That question's already been asked this round."); return; }
    if (idn && vetoed.has(idn)) q.reaskDouble = true;
    client.postMessage(client.seekerKey, text, type![0].toUpperCase() + type!.slice(1), { question: q });
  }

  // ---- answers + applying ------------------------------------------------
  function elimsFor(q: Question, answer: string): string[] {
    const newOnly = (ids: string[]) => ids.filter((id) => client.statusOf(id) === "active");
    if (q.type === "radar" && q.ref && q.dist != null) return newOnly(radarEliminate(q.ref, q.dist, answer === "yes" ? "yes" : "no"));
    if (q.type === "thermometer" && q.a && q.b) return newOnly(thermometerEliminate(q.a, q.b, answer === "hotter" ? "hotter" : "colder"));
    if (q.type === "matching" && q.ref && q.catId) return matchingEliminate(q.catId, q.ref[0], q.ref[1], answer === "yes" ? "yes" : "no", (id) => client.statusOf(id));
    if (q.type === "measuring" && q.catId && q.dist != null) return measuringEliminate(q.catId, q.dist, answer === "closer" ? "closer" : "further", (id) => client.statusOf(id));
    return [];
  }
  function applyQ(q: Question, answer: string) {
    const ids = elimsFor(q, answer);
    // Label the elimination with the actual question text → the recap GIF captions it.
    if (ids.length) { const qt = (client.threads[client.seekerKey] || []).find((m) => m.question?.id === q.id)?.text; client.eliminate(ids, `${qt || cap(q.type)} → ${cap(answer)}`); }
    applied.add(q.id); persistApplied(); renderLog();
  }
  // Leader records the hider's out-of-band answer into the shared log.
  function recordAnswer(qId: string, answer: string, label: string) {
    if (!client.isLeader) return;
    client.postMessage(client.seekerKey, `📝 Recorded: ${label}`, undefined, { answer, replyTo: qId });
  }
  const cap = (s: string) => s[0].toUpperCase() + s.slice(1);

  // ---- one-question-per-slot bookkeeping ---------------------------------
  // Each question can be asked ONCE per round: every radar distance (¼/½/1/3/5),
  // ONE custom radar, and each matching / measuring category / photo type. A
  // VETOED question frees its slot and, re-asked, DOUBLES the hider's draw.
  // Withdrawing (deleting) a question also frees its slot.
  const RADAR_PRESET_MS = RADAR_DISTS.map((d) => d.m);
  const radarSlot = (dist?: number) => (dist != null && RADAR_PRESET_MS.includes(dist) ? `r:${dist}` : "r:custom");
  function qIdentity(q: Question): string | null {
    if (q.type === "radar") return radarSlot(q.dist);
    if (q.type === "matching" || q.type === "measuring") return q.catId ? `${q.type}:${q.catId}` : null;
    if (q.type === "photo") return q.photoType ? `photo:${q.photoType}` : null;
    return null; // thermometer isn't slot-limited
  }
  // used = slots locked (open or answered); vetoed = freed-but-doubles-on-reask.
  function questionUsage(): { used: Set<string>; vetoed: Set<string> } {
    const thread = client.threads[client.seekerKey] || [];
    const used = new Set<string>(), vetoed = new Set<string>();
    for (const m of thread) {
      const q = m.question; if (!q?.id || m.fromHider || m.deleted) continue;
      const idn = qIdentity(q); if (!idn) continue;
      if (m.vetoed) vetoed.add(idn); else used.add(idn);
    }
    for (const v of [...vetoed]) if (used.has(v)) vetoed.delete(v);
    return { used, vetoed };
  }

  // ---- render: builder ---------------------------------------------------
  const TYPES: { id: QType; label: string; emoji: string; ex: string }[] = [
    { id: "matching", label: "Matching", emoji: "🟰", ex: "Are you in the same borough as me?" },
    { id: "measuring", label: "Measuring", emoji: "📏", ex: "Are you closer to or further from an airport than me?" },
    { id: "thermometer", label: "Thermo", emoji: "🌡", ex: "I traveled ½ mi — am I hotter or colder?" },
    { id: "radar", label: "Radar", emoji: "📡", ex: "Are you within ½ mi of my spot?" },
    { id: "photo", label: "Photo", emoji: "📷", ex: "Send a photo of the tallest building you can see." },
  ];
  function photoControls(): string {
    const types = enabledPhotoTypes(client.config.photoTypes);
    if (!types.length) return `<div class="ask-mine">No photo requests are enabled. Ask the GM to turn some on.</div>`;
    const sel = photoType ? PHOTO_BY_ID[photoType] : null;
    return `<div class="q-lbl">REQUEST A PHOTO</div>
      <div class="ask-photogrid">${types.map((p) => `<button class="ask-cat ${p.id === photoType ? "on" : ""}" data-ptype="${p.id}">${esc(p.name)}</button>`).join("")}</div>
      ${sel ? `<div class="ask-mine"><b>${esc(sel.name)}</b><br>${esc(sel.sd)}</div>` : ""}`;
  }
  function refRow(): string {
    return `<div class="sk-mode-seg sub ask-refs">
        <button data-ref="gps" class="${refMode === "gps" ? "on" : ""}">📍 My location</button>
        <button data-ref="pin" class="${refMode === "pin" ? "on" : ""}">📌 Pin</button>
        <button data-ref="addr" class="${refMode === "addr" ? "on" : ""}">🔤 Address</button>
      </div>
      ${refMode === "addr" ? `<div class="meas-addrrow"><input class="sk-search" id="ask-addr" placeholder="address / corner…"/><button class="meas-find" id="ask-find">Find</button></div>` : ""}`;
  }
  function landmarkControls(): string {
    const cats = type === "matching" ? matchingCategories() : measuringCategories();
    const { used, vetoed } = questionUsage();
    return `${refRow()}
      <div class="ask-catgrid">${cats.map((c) => {
        const slot = `${type}:${c.id}`; const u = used.has(slot), v = vetoed.has(slot);
        // Used categories stay SELECTABLE (so you can still measure/test to them to
        // narrow down on the answer) — only re-ASKING is blocked (the Send button).
        return `<button class="ask-cat ${c.id === catId ? "on" : ""} ${u ? "ask-used" : ""} ${v ? "ask-veto2x" : ""}" data-cat="${c.id}" title="${u ? "already asked — tap to measure to it (can't re-ask)" : v ? "vetoed — re-ask for DOUBLE the draw" : ""}">${c.emoji} ${esc(c.label)}${v ? " ×2" : ""}</button>`;
      }).join("")}</div>`;
  }
  function radarControls(): string {
    const { used, vetoed } = questionUsage();
    const btn = (slot: string, val: string, label: string, on: boolean) => {
      const u = used.has(slot), v = vetoed.has(slot);
      // Used distances stay selectable (draw the ring to test) — only re-ask is blocked.
      return `<button data-rd="${val}" class="${on ? "on" : ""} ${u ? "ask-used" : ""} ${v ? "ask-veto2x" : ""}" title="${u ? "already asked — tap to draw the ring (can't re-ask)" : v ? "vetoed — re-ask for DOUBLE the draw" : ""}">${label}${v ? " ×2" : ""}</button>`;
    };
    return `${refRow()}<div class="q-lbl">WITHIN HOW FAR?</div>
      <div class="sk-seg6">${RADAR_DISTS.map((d) => btn(`r:${d.m}`, `${d.m}`, d.l, !radarCustom && d.m === radarDist)).join("")}${btn("r:custom", "custom", "Custom", radarCustom)}</div>
      ${radarCustom ? `<div class="ask-customradar"><input id="ask-radar-custom" class="sk-search" type="number" min="0.05" step="0.05" value="${radarDist}"/><span class="sub">miles</span></div>` : ""}`;
  }
  function thermoControls(): string {
    return `<div class="ask-mine">Drag <b style="color:var(--blue)">A</b> (start) &amp; <b style="color:var(--red)">B</b> (where you traveled) on the map.</div>
      <div class="q-lbl">OR JUMP THE TRAVEL DISTANCE</div>
      <div class="sk-seg6">${THERMO_DISTS.map((t) => `<button data-td="${t.m}">${t.l}</button>`).join("")}<button data-td="custom">drag</button></div>`;
  }
  function renderBuilder() {
    { const ae = document.activeElement as HTMLElement; if (builderEl.contains(ae) && (ae?.id === "ask-text" || ae?.id === "ask-radar-custom")) return; } // don't clobber editing
    const lead = client.isLeader;
    // The current selection's slot — used to block re-ASKING (Send) while still
    // letting the seeker select & measure to an already-asked category/distance.
    const { used: usedSlots } = questionUsage();
    const curSlot = (type === "matching" || type === "measuring") ? `${type}:${catId}`
      : type === "radar" ? radarSlot(radarDist)
      : type === "photo" ? (photoType ? `photo:${photoType}` : null) : null;
    const slotUsed = !!curSlot && usedSlots.has(curSlot);
    const note = !client.seekerKey ? "" : lead ? "" : client.hasLeader
      ? `Only the team leader (${esc(nameOf(client.team?.leaderId))}) sends questions to the hider — copy it to coordinate, or push your map to them.`
      : `No team leader yet — only the leader sends questions. <button class="link-btn" id="ask-claim">Claim team leader</button>`;
    builderEl.innerHTML = `
      <div class="ask-typeseg">${TYPES.map((t) => `<button class="ask-type ${type === t.id ? "on" : ""}" data-type="${t.id}"><span>${t.emoji}</span>${t.label}</button>`).join("")}</div>
      ${!type ? `<div class="ask-legend">
          <div class="ask-legrow"><b>🟰 Matching</b><span>“Is your nearest X the same as mine?”</span></div>
          <div class="ask-legrow"><b>📏 Measuring</b><span>“Closer to or further from an X?”</span></div>
          <div class="ask-legrow"><b>🌡 Thermo</b><span>“I traveled X. Hotter or colder?”</span></div>
          <div class="ask-legrow"><b>📡 Radar</b><span>“Are you within X of me?”</span></div>
          <div class="ask-legrow"><b>📷 Photo</b><span>Ask the hider for a photo (they reply with one)</span></div>
          <p class="sk-note">Pick a type → the app computes your answer & writes the question. Edit it, copy it, or send it to the hider.</p>
        </div>` : `
        <div class="ask-mine" style="margin-bottom:6px">💡 <b>Example</b> — “${esc(TYPES.find((t) => t.id === type)?.ex ?? "")}”</div>
        ${type === "matching" || type === "measuring" ? landmarkControls() : type === "radar" ? radarControls() : type === "photo" ? photoControls() : thermoControls()}
        <div class="q-lbl" style="margin-top:8px">QUESTION (editable)</div>
        <textarea id="ask-text" class="ask-textarea" rows="3" placeholder="${ready() ? "" : "set your location / points first"}">${esc(draft)}</textarea>
        <div class="ask-actions">
          <button class="ghost ask-copy" id="ask-copy" ${ready() ? "" : "disabled"}>📋 Copy</button>
          <button class="primary commit" id="ask-send" ${lead && ready() && !slotUsed && !client.hasOpenQuestion(client.seekerKey) ? "" : "disabled"}>📩 Send to hider</button>
        </div>
        ${client.hasOpenQuestion(client.seekerKey)
          ? `<p class="sk-note" style="color:var(--orange,#FF6319)">⏳ One question at a time — wait for the hider to answer your open question (or withdraw it) before asking another.</p>`
          : slotUsed
          ? `<p class="sk-note" style="color:var(--orange,#FF6319)">✓ Already asked this round — measure/test to it freely, but pick another ${type === "radar" ? "distance" : "category"} (or type) to ask again.</p>`
          : note ? `<p class="sk-note" style="color:var(--orange,#FF6319)">${note}</p>` : ""}`}`;
    wireBuilder();
  }
  function wireBuilder() {
    builderEl.querySelectorAll<HTMLButtonElement>("[data-type]").forEach((b) => b.addEventListener("click", () => { type = b.dataset.type as QType; if (type === "thermometer") startThermo(); else gameMap.clearToolMarkers(); recompute(); }));
    builderEl.querySelectorAll<HTMLButtonElement>("[data-ref]").forEach((b) => b.addEventListener("click", () => pickRef(b.dataset.ref as "gps" | "pin" | "addr")));
    builderEl.querySelector("#ask-find")?.addEventListener("click", () => { const v = builderEl.querySelector<HTMLInputElement>("#ask-addr")?.value.trim(); if (v) findAddress(v); });
    builderEl.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((b) => b.addEventListener("click", () => { catId = b.dataset.cat!; recompute(); }));
    builderEl.querySelectorAll<HTMLButtonElement>("[data-rd]").forEach((b) => b.addEventListener("click", () => { if (b.dataset.rd === "custom") radarCustom = true; else { radarCustom = false; radarDist = +b.dataset.rd!; } recompute(); }));
    builderEl.querySelector<HTMLInputElement>("#ask-radar-custom")?.addEventListener("input", (e) => { const v = +(e.target as HTMLInputElement).value; if (v > 0) { radarDist = v; draft = computeText(); redraw(); const ta = builderEl.querySelector<HTMLTextAreaElement>("#ask-text"); if (ta) ta.value = draft; } });
    builderEl.querySelectorAll<HTMLButtonElement>("[data-ptype]").forEach((b) => b.addEventListener("click", () => { photoType = b.dataset.ptype!; recompute(); }));
    builderEl.querySelectorAll<HTMLButtonElement>("[data-td]").forEach((b) => b.addEventListener("click", () => { if (b.dataset.td === "custom") opts?.setFullscreen?.(true); else jumpThermo(+b.dataset.td!); }));
    builderEl.querySelector<HTMLTextAreaElement>("#ask-text")?.addEventListener("input", (e) => { draft = (e.target as HTMLTextAreaElement).value; });
    builderEl.querySelector("#ask-copy")?.addEventListener("click", () => { const t = (builderEl.querySelector<HTMLTextAreaElement>("#ask-text")?.value || draft).trim(); if (t) { copy(t); const b = builderEl.querySelector("#ask-copy"); if (b) { b.textContent = "✓ Copied"; setTimeout(() => { if (b) b.textContent = "📋 Copy"; }, 1200); } } });
    builderEl.querySelector("#ask-send")?.addEventListener("click", () => { send(); });
    builderEl.querySelector("#ask-claim")?.addEventListener("click", () => { client.claimLeader(); client.setView("canonical"); });
  }

  // ---- render: the shared question log -----------------------------------
  function renderLog() {
    const thread: Msg[] = client.threads[client.seekerKey] || [];
    const questions = thread.filter((m) => m.question?.id);
    // Standalone hider→seeker system lines (curse casts, moves, randomize notices)
    // carry no question/answer, so they'd otherwise never surface in the log.
    const notices = thread.filter((m) => m.text && !m.question?.id && !m.replyTo);
    const answers = new Map<string, { answer: string; fromHider?: boolean; by: string }>();
    const photos = new Map<string, string>(); // questionId → photoKey (a photo reply)
    for (const m of thread) {
      if (m.replyTo && m.answer) answers.set(m.replyTo, { answer: m.answer, fromHider: m.fromHider, by: m.fromName });
      if (m.replyTo && m.photoKey) photos.set(m.replyTo, m.photoKey);
    }
    const auto = autoApply();
    const lead = client.isLeader;
    logEl.innerHTML = `
      ${notices.length ? `<div class="sk-seclabel" style="margin-top:14px">NOTICES</div>
        ${notices.slice().reverse().map((m) => `<div class="ask-lognotice">${esc(m.text)}</div>`).join("")}` : ""}
      <div class="sk-seclabel" style="margin-top:14px">QUESTION LOG <span class="sk-on-count">${questions.length}</span></div>
      ${questions.length ? questions.slice().reverse().map((m) => {
        const q = m.question!; const a = answers.get(q.id); const done = applied.has(q.id);
        const ansLabels = ANSWER_LABELS[q.type] || [];
        const photoKey = photos.get(q.id);
        const unanswered = !a && !photoKey && !m.vetoed && !m.deleted && !m.randomized && !m.closed;
        return `<div class="ask-logitem">
          <div class="ask-logq ${m.deleted ? "ask-deleted" : ""}">${esc(m.text)}<button class="ask-copy2" data-copytext="${esc(m.text)}" title="Copy">📋</button>${lead && unanswered ? `<button class="ask-copy2 ask-del" data-delq="${esc(m.id)}" title="Withdraw this question — strikes it through for both teams (only works before it's answered)">🗑</button>` : ""}</div>
          ${photoKey ? `<img class="ask-logphoto" src="${photoUrl(photoKey)}" loading="lazy" alt="hider photo reply"/>` : ""}
          ${m.deleted
            ? `<div class="ask-logans" style="color:var(--muted)">🗑 You withdrew this question.</div>`
            : m.randomized
            ? `<div class="ask-logans" style="color:var(--blue)">🎲 Randomized by the hider — they answered a random same-category question instead.</div>`
            : m.vetoed
            ? `<div class="ask-logans" style="color:var(--red);font-weight:700">🚫 Vetoed by the hider — no answer, no card draw.</div>`
            : m.closed
            ? `<div class="ask-logans"><span style="color:var(--green)">✓ The hider marked this answered.</span></div>`
            : q.type === "photo"
            ? `<div class="ask-logans ${photoKey ? "" : "muted"}">${photoKey ? `<span style="color:var(--green)">✓ photo received</span>` : "📷 awaiting the hider's photo…"}</div>`
            : a
            ? `<div class="ask-logans">${a.fromHider ? "🫥 Hider" : "📝 " + esc(a.by)} said <b>${esc(a.answer)}</b>${done ? ` · <span style="color:var(--green)">✓ applied</span>` : ""}</div>
               ${!done && auto ? `<button class="primary green ask-apply" data-apply="${esc(q.id)}" data-ans="${esc(a.answer)}">Apply — eliminate ${elimsFor(q, a.answer).length}</button>` : ""}`
            : lead
              ? `<div class="ask-recordrow"><span class="ask-rec-lbl">Record the answer:</span>${ansLabels.map(([v, l]) => `<button class="ghost ask-rec" data-rec="${esc(q.id)}" data-recval="${v}" data-reclabel="${esc(l)}">${esc(l)}</button>`).join("")}</div>`
              : `<div class="ask-logans muted">awaiting answer…</div>`}
        </div>`;
      }).join("") : `<div class="sk-empty">No questions asked yet${lead ? "" : " by your team"}.</div>`}
      <label class="ask-autotoggle"><input type="checkbox" id="ask-auto" ${auto ? "checked" : ""}/> One-tap apply (off = manual calculation)</label>`;
    logEl.querySelectorAll<HTMLButtonElement>("[data-copytext]").forEach((b) => b.addEventListener("click", () => copy(b.dataset.copytext!)));
    logEl.querySelectorAll<HTMLButtonElement>("[data-apply]").forEach((b) => b.addEventListener("click", () => { const q = findQ(b.dataset.apply!); if (q) applyQ(q, b.dataset.ans!); }));
    logEl.querySelectorAll<HTMLButtonElement>("[data-rec]").forEach((b) => b.addEventListener("click", () => recordAnswer(b.dataset.rec!, b.dataset.recval!, b.dataset.reclabel!)));
    logEl.querySelectorAll<HTMLButtonElement>("[data-delq]").forEach((b) => b.addEventListener("click", () => { if (confirm("Delete this question? It hasn't been answered yet, so it will also disappear from the hider's queue.")) client.deleteMessage(client.seekerKey, b.dataset.delq!); }));
    logEl.querySelector<HTMLInputElement>("#ask-auto")?.addEventListener("change", (e) => setAutoApply((e.target as HTMLInputElement).checked));
  }
  function findQ(id: string): Question | null { const t: Msg[] = client.threads[client.seekerKey] || []; return t.find((m) => m.question?.id === id)?.question ?? null; }

  // ---- thermometer pins --------------------------------------------------
  async function resolveThermoPlace() { if (!tA) return; const seq = ++cornerSeq; const ll = tA; const c = await nearestCorner(ll[1], ll[0]); if (seq === cornerSeq && c) { tPlace = c; recompute(); } }
  function startThermo() {
    gameMap.onToolExit = resetTool;
    gameMap.clearToolMarkers(); const c = gameMap.map.getCenter();
    tA = [c.lng - 0.008, c.lat]; tB = [c.lng + 0.008, c.lat];
    gameMap.addToolMarker(tA, "#2980b9", true, (ll) => { tA = ll; drawThermo(); resolveThermoPlace(); recompute(); }, true, "A");
    gameMap.addToolMarker(tB, "#c0392b", true, (ll) => { tB = ll; drawThermo(); recompute(); }, true, "B");
    drawThermo(); resolveThermoPlace();
  }
  function drawThermo() { if (tA && tB) gameMap.setPreviewGeometry([turf.lineString([tA, tB])]); }
  function jumpThermo(mi: number) { if (!tA) startThermo(); const c = tA!; tB = [c[0] + mi / (49.5 * Math.cos((c[1] * Math.PI) / 180)), c[1]]; gameMap.clearToolMarkers(); gameMap.addToolMarker(tA!, "#2980b9", true, (ll) => { tA = ll; drawThermo(); recompute(); }, true, "A"); gameMap.addToolMarker(tB, "#c0392b", true, (ll) => { tB = ll; drawThermo(); recompute(); }, true, "B"); drawThermo(); recompute(); }

  const unsub = client.subscribe(() => { if (mount.offsetParent) { renderLog(); renderBuilder(); } });

  return {
    enter() { renderBuilder(); renderLog(); if (type === "thermometer") startThermo(); else redraw(); },
    leave() { stopGps(); clearMap(); gameMap.onToolExit = null; unsub?.(); },
  };
}

export { CATEGORIES };
